import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import React, { useMemo } from 'react';
import EmoteViewer from '../../extensions/extension-emote/viewer';
import DaVideoViewer from '../../extensions/extension-video/Viewer';
import DaDeviationViewer from '../../extensions/extension-deviation/Viewer/Viewer';
import DaDeviationThumbViewer from '../../extensions/extension-deviation-thumb/Viewer/Viewer';
import DaGalleryViewer from '../../extensions/extension-gallery/Viewer/Viewer';
import MentionViewer from '../../extensions/extension-mention/Viewer/Viewer';
import HashtagViewer from '../../extensions/extension-hashtag/Viewer/Viewer';
import GifViewer from '../../extensions/extension-gif/Viewer/Viewer';
import { elmoSaysThisMightBeADeviationUrl } from '../../extensions/extension-deviation/helpers';
import { scrollIntoViewInViewportCenter } from '@wix/da-ds/pkg/utils/scrollIntoViewWithHeader';
export function cleanLinkSrc(linkOrSrc, allowedDomains = []) {
    if (!/^(http|https|mailto):\/\//.test(linkOrSrc)) {
        return '';
    }
    if (allowedDomains.length > 0) {
        const url = new URL(linkOrSrc);
        if (!url) {
            return '';
        }
        for (const domain of allowedDomains){
            if (url.hostname.endsWith(domain)) {
                return linkOrSrc;
            }
        }
        // No matches on allowed domains, drop out
        return '';
    } else {
        return linkOrSrc;
    }
}
const Heading = (props)=>{
    var _props_textAlign;
    const style = {
        textAlign: (_props_textAlign = props.textAlign) !== null && _props_textAlign !== void 0 ? _props_textAlign : undefined
    };
    switch(`${props.level}`){
        case '2':
            return /*#__PURE__*/ React.createElement("h2", {
                style: style
            }, props.children);
        case '3':
            return /*#__PURE__*/ React.createElement("h3", {
                style: style
            }, props.children);
        default:
            return /*#__PURE__*/ React.createElement("h4", {
                style: style
            }, props.children);
    }
};
function makeMark(extConfigValue, handler) {
    if (extConfigValue) {
        return handler;
    }
    return (props)=>/*#__PURE__*/ React.createElement(React.Fragment, null, props.children);
}
function makeNode(extConfigValue, handler) {
    if (extConfigValue) {
        return handler;
    }
    return (props)=>/*#__PURE__*/ React.createElement(React.Fragment, null, props.children);
}
function isInternalLink(url) {
    return /^https:\/\/www\.deviantart\.com/.test(url) || /^#/.test(url) || elmoSaysThisMightBeADeviationUrl(url);
}
export const useHandlers = (extConfig)=>{
    const handlers = useMemo(()=>{
        const markHandlers = {
            link: makeMark(extConfig.link, (props)=>{
                var _props_node_attrs, _props_node;
                var _props_node_attrs_href;
                const href = (_props_node_attrs_href = props === null || props === void 0 ? void 0 : (_props_node = props.node) === null || _props_node === void 0 ? void 0 : (_props_node_attrs = _props_node.attrs) === null || _props_node_attrs === void 0 ? void 0 : _props_node_attrs.href) !== null && _props_node_attrs_href !== void 0 ? _props_node_attrs_href : '';
                let handleClick = undefined;
                if (href.startsWith('#')) {
                    handleClick = (e)=>{
                        const el = document.getElementById(href.replace('#', ''));
                        if (el) {
                            e.preventDefault();
                            scrollIntoViewInViewportCenter(el, undefined, undefined, true);
                        }
                    };
                }
                return /*#__PURE__*/ React.createElement("a", {
                    onClick: handleClick,
                    href: cleanLinkSrc(href),
                    target: isInternalLink(href) ? undefined : '_blank',
                    rel: "noopener noreferrer nofollow ugc"
                }, props.children);
            }),
            bold: makeMark(extConfig.bold, (props)=>/*#__PURE__*/ React.createElement("strong", null, props.children)),
            italic: makeMark(extConfig.italic, (props)=>/*#__PURE__*/ React.createElement("em", null, props.children)),
            underline: makeMark(extConfig.underline, (props)=>/*#__PURE__*/ React.createElement("u", null, props.children)),
            strike: makeMark(extConfig.strikethrough, (props)=>/*#__PURE__*/ React.createElement("s", null, props.children)),
            code: (props)=>/*#__PURE__*/ React.createElement(React.Fragment, null, props.children),
            textStyle: makeMark(extConfig.textColor || extConfig.textFont, (props)=>{
                var _props_node_attrs, _props_node_attrs1;
                const styles = {};
                if (((_props_node_attrs = props.node.attrs) === null || _props_node_attrs === void 0 ? void 0 : _props_node_attrs.fontFamily) && extConfig.textFont) {
                    styles.fontFamily = props.node.attrs.fontFamily;
                }
                if (((_props_node_attrs1 = props.node.attrs) === null || _props_node_attrs1 === void 0 ? void 0 : _props_node_attrs1.color) && extConfig.textColor) {
                    styles.color = props.node.attrs.color;
                }
                return /*#__PURE__*/ React.createElement("span", {
                    style: styles
                }, props.children);
            }),
            highlight: makeMark(extConfig.textHighlight, (props)=>{
                var _props_node_attrs;
                return /*#__PURE__*/ React.createElement("mark", {
                    style: {
                        backgroundColor: (_props_node_attrs = props.node.attrs) === null || _props_node_attrs === void 0 ? void 0 : _props_node_attrs.color
                    }
                }, props.children);
            })
        };
        const nodeHandlers = {
            'link-wrapper': (props)=>{
                return /*#__PURE__*/ React.createElement(React.Fragment, null, props.children);
            },
            doc: (props)=>/*#__PURE__*/ React.createElement(React.Fragment, null, props.children),
            text: (props)=>/*#__PURE__*/ React.createElement(React.Fragment, null, props.node.text),
            paragraph: (props)=>{
                var _props_node_attrs, _props_node_attrs1, _props_node_attrs2;
                var _props_node_attrs_indentation;
                return /*#__PURE__*/ React.createElement("p", {
                    id: (_props_node_attrs = props.node.attrs) === null || _props_node_attrs === void 0 ? void 0 : _props_node_attrs.id,
                    style: {
                        textAlign: (_props_node_attrs1 = props.node.attrs) === null || _props_node_attrs1 === void 0 ? void 0 : _props_node_attrs1.textAlign,
                        marginInlineStart: `${((_props_node_attrs_indentation = (_props_node_attrs2 = props.node.attrs) === null || _props_node_attrs2 === void 0 ? void 0 : _props_node_attrs2.indentation) !== null && _props_node_attrs_indentation !== void 0 ? _props_node_attrs_indentation : 0) * 24}px`
                    }
                }, props.children);
            },
            heading: makeNode(extConfig.heading, (props)=>{
                var _props_node_attrs, _props_node_attrs1;
                var _props_node_attrs_indentation;
                return /*#__PURE__*/ React.createElement(Heading, {
                    level: (_props_node_attrs = props.node.attrs) === null || _props_node_attrs === void 0 ? void 0 : _props_node_attrs.level,
                    textAlign: props.node.attrs.textAlign
                }, /*#__PURE__*/ React.createElement("span", {
                    style: {
                        marginInlineStart: `${((_props_node_attrs_indentation = (_props_node_attrs1 = props.node.attrs) === null || _props_node_attrs1 === void 0 ? void 0 : _props_node_attrs1.indentation) !== null && _props_node_attrs_indentation !== void 0 ? _props_node_attrs_indentation : 0) * 24}px`
                    }
                }, props.children));
            }),
            hardBreak: ()=>/*#__PURE__*/ React.createElement("br", null),
            bulletList: makeNode(extConfig.bulletList, (props)=>/*#__PURE__*/ React.createElement("ul", null, props.children)),
            orderedList: makeNode(extConfig.orderedList, (props)=>/*#__PURE__*/ React.createElement("ol", null, props.children)),
            listItem: makeNode(extConfig.bulletList || extConfig.orderedList, (props)=>/*#__PURE__*/ React.createElement("li", null, props.children)),
            codeBlock: (props)=>/*#__PURE__*/ React.createElement(React.Fragment, null, props.children),
            horizontalRule: makeNode(extConfig.horizontalRuler, ()=>/*#__PURE__*/ React.createElement("hr", null)),
            blockquote: makeNode(extConfig.blockquote, (props)=>/*#__PURE__*/ React.createElement("blockquote", null, props.children)),
            mention: makeNode(extConfig.mention, (props)=>{
                var _props_node_attrs, _props_node_attrs1, _props_node_attrs2;
                return /*#__PURE__*/ React.createElement("span", {
                    "data-type": "mention",
                    className: "mention",
                    "data-id": (_props_node_attrs = props.node.attrs) === null || _props_node_attrs === void 0 ? void 0 : _props_node_attrs.id
                }, /*#__PURE__*/ React.createElement("a", {
                    href: `https://www.deviantart.com/${(_props_node_attrs1 = props.node.attrs) === null || _props_node_attrs1 === void 0 ? void 0 : _props_node_attrs1.id}`
                }, (_props_node_attrs2 = props.node.attrs) === null || _props_node_attrs2 === void 0 ? void 0 : _props_node_attrs2.id));
            }),
            'da-mention': makeNode(extConfig.mention, MentionViewer),
            'da-gif': makeNode(extConfig.gif, GifViewer),
            anchor: makeNode(extConfig.anchor, (props)=>{
                var _props_node_attrs;
                return(// eslint-disable-next-line jsx-a11y/anchor-has-content
                /*#__PURE__*/ React.createElement("a", {
                    id: (_props_node_attrs = props.node.attrs) === null || _props_node_attrs === void 0 ? void 0 : _props_node_attrs.id,
                    "data-testid": "anchor"
                }));
            }),
            'da-emote': makeNode(extConfig.emote, EmoteViewer),
            'da-video': makeNode(extConfig.emote, DaVideoViewer),
            'da-deviation': makeNode(extConfig.deviation, DaDeviationViewer),
            'da-gallery': makeNode(extConfig.gallery, DaGalleryViewer),
            'da-hashtag': makeNode(extConfig.hashtag, HashtagViewer),
            'da-deviation-thumb': makeNode(extConfig.deviationThumb, DaDeviationThumbViewer)
        };
        const handlerConfig = _object_spread({}, markHandlers, nodeHandlers);
        return handlerConfig;
    }, [
        extConfig.anchor,
        extConfig.blockquote,
        extConfig.bold,
        extConfig.bulletList,
        extConfig.deviation,
        extConfig.deviationThumb,
        extConfig.emote,
        extConfig.gallery,
        extConfig.hashtag,
        extConfig.gif,
        extConfig.heading,
        extConfig.horizontalRuler,
        extConfig.italic,
        extConfig.link,
        extConfig.mention,
        extConfig.orderedList,
        extConfig.strikethrough,
        extConfig.textColor,
        extConfig.textFont,
        extConfig.textHighlight,
        extConfig.underline
    ]);
    return handlers;
};

//# sourceMappingURL=useHandlers.js.map